import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../builder/components/layout/AppLayout';
import Table from '../../shared/components/layout/Table';
import Button from '../../shared/components/ui/Button';
import Search from '../../shared/components/ui/Search';
import Header from '../../shared/components/layout/Header';
import Modal from '../../shared/components/ui/Modal';
import { transformData } from '../../super-admin/utils/transformData';
import AddLand from '../components/forms/AddLand';
import EditLand from '../components/forms/EditLand';
import {
  createLand,
  editLandById,
  getAllLands,
  handleCheckLandDelete,
  deleteLand
} from '../services/api/implementation/impl';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { filterData } from '../../shared/utils/filterData';
import NoDataFound from '../components/layout/NoDataFound';
import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Loader from '../../shared/components/ui/Loader';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import { Plus } from 'lucide-react';

/**
 * Converts a numeric value to a string with up to 2 decimals,
 * or returns it as an integer if the decimals are `.00`.
 */
const formatNumber = (input) => {
  const userId = Cookies.get('userId');
  const number = parseFloat(input);
  if (isNaN(number)) {
    return 'N/A';
  }

  const fixedNumber = number.toFixed(2);

  if (fixedNumber.split('.')[1] === '00') {
    return parseInt(fixedNumber, 10);
  }

  return fixedNumber; // Return as a string with up to 2 decimals
};

const parseAreaType = (areaType) => {
  if (!areaType) return '';

  let type = areaType.toLowerCase().trim();
  type = type.replace('sq/', '');
  type = type.replace(/\s+/g, '');

  if (type.includes('guntha')) type = type.replace('guntha', 'gunta');
  if (type === 'gunta' || type === 'guntha') type = 'gunta';

  if (type === 'acres') type = 'acre';

  if (type === 'hectares') type = 'hectare';

  if (type === 'cent' || type === 'cents') type = 'cents';

  return type;
};

const calculateFinalArea = (mainArea, mainAreaType, subArea, subAreaType) => {
  const mArea = parseFloat(mainArea) || 0;
  const sArea = parseFloat(subArea) || 0;

  const mType = parseAreaType(mainAreaType);
  const sType = parseAreaType(subAreaType);

  let finalMainArea = mArea;
  let finalMainAreaType = mType;
  let finalSubArea = sArea;
  let finalSubAreaType = sType;

  if (sType === 'gunta') {
    // CASE 1: main area is acre
    if (mType === 'acre') {
      const gunthaPerAcre = 40; // 1 acre = 40 gunta
      // How many acres do we get from the sub-area?
      const acresFromSub = Math.floor(sArea / gunthaPerAcre);
      const leftoverGunta = sArea % gunthaPerAcre;

      finalMainArea = mArea + acresFromSub;
      finalMainAreaType = 'acre';
      finalSubArea = leftoverGunta;
      finalSubAreaType = 'gunta';
    }
    // CASE 2: main area is hectare
    else if (mType === 'hectare') {
      const gunthaPerHectare = 98.84; // 1 hectare ~ 98.84 gunta
      // Convert leftover gunta to hectares
      const totalHectaresFromSub = sArea / gunthaPerHectare;

      // The integer portion to add to main area
      const integerHectares = Math.floor(totalHectaresFromSub);
      // Fractional portion
      const fractionalHectares = totalHectaresFromSub - integerHectares;

      finalMainArea = mArea + integerHectares;
      finalMainAreaType = 'hectare';

      // leftover gunta = fractional part * 98.84
      const leftoverGunta = fractionalHectares * gunthaPerHectare;
      finalSubArea = leftoverGunta;
      finalSubAreaType = 'gunta';
    }
  }
  // CASE 3: Sub area is in cents
  else if (sType === 'cents') {
    // CASE 3.1: main area is acre
    if (mType === 'acre') {
      const centsPerAcre = 100; // 1 acre = 100 cents
      // How many acres do we get from the sub-area?
      const acresFromSub = Math.floor(sArea / centsPerAcre);
      const leftoverCents = sArea % centsPerAcre;

      finalMainArea = mArea + acresFromSub;
      finalMainAreaType = 'acre';
      finalSubArea = leftoverCents;
      finalSubAreaType = 'cents';
    }
    // CASE 3.2: main area is hectare
    else if (mType === 'hectare') {
      const centsPerHectare = 247.105; // 1 hectare ~ 247.105 cents
      // Convert leftover cents to hectares
      const totalHectaresFromSub = sArea / centsPerHectare;

      // The integer portion to add to main area
      const integerHectares = Math.floor(totalHectaresFromSub);
      // Fractional portion
      const fractionalHectares = totalHectaresFromSub - integerHectares;

      finalMainArea = mArea + integerHectares;
      finalMainAreaType = 'hectare';

      // leftover cents = fractional part * 247.105
      const leftoverCents = fractionalHectares * centsPerHectare;
      finalSubArea = leftoverCents;
      finalSubAreaType = 'cents';
    }
  }

  return { finalMainArea, finalMainAreaType, finalSubArea, finalSubAreaType };
};

const LandsList = () => {
  /**
   * Hooks
   */
  const navigate = useNavigate();
  const userId = Cookies.get('userId');

  /**
   * Local state
   */
  const { collapsed } = useSidebar();
  const [isOpenAddLand, setIsOpenAddLand] = useState(false);
  const [isOpenEditLand, setIsOpenEditLand] = useState(false);
  const [LandsStaticId, setLandsStaticId] = useState(null);
  const [filterLand, setFilterLand] = useState([]);
  const [landQuery, setLandQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);

  /**
   * Meta data
   */
  const landsFormat = ['id', 'name', 'number', 'area', 'tehsil', 'actions'];

  /**
   * Data fetching
   */
  const {
    data: lands,
    refetch: refetchLands,
    isError,
    isLoading
  } = useQuery({
    queryKey: ['lands'],
    queryFn: getAllLands
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Lands List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    setFilterLand(lands);
  }, [lands]);

  useEffect(() => {
    refetchLands();
  }, [refetchLands]);

  /**
   * Data mutation
   */
  const { mutate: mutateLand } = useMutation({
    mutationFn: (land) => createLand(land),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Land created successfully');
      // setIsOpenAddLand(false);
      await refetchLands(); // UI data update
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new land', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating land');
    }
  });

  const { mutate: handleEditLand } = useMutation({
    mutationFn: (updatedLand) => {
      // Log the payload for debugging
      console.log('Updated Land Payload:', updatedLand);
      return editLandById(parseInt(LandsStaticId), updatedLand);
    },
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Land edited successfully');
      setIsOpenEditLand(false);
      window.location.reload();
      await refetchLands();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error editing land:', err); // Log the error for debugging
      toast.error(err?.response?.data?.message || err?.message || 'Error editing land');
    }
  });

  /**
   * Helper method calls
   */
  const handleSearchChange = useCallback(
    (incomingQuery) => {
      setLandQuery(incomingQuery);
      if (incomingQuery) {
        const filtered = filterData('name', lands, incomingQuery);
        setFilterLand(filtered);
      } else {
        setFilterLand(lands);
      }
    },
    [lands]
  );

  /**
   * We prepare the data for the table by:
   *   - Collecting all khasra (or survey) numbers
   *   - Calculating final main/sub area if needed
   *   - Formatting the final area string
   */
  const landsFormatted = filterLand?.map((item) => {
    let allKhasraNumbers = [];

    if (Array.isArray(item?.ownerLandMappings)) {
      item.ownerLandMappings.forEach((mapping) => {
        if (Array.isArray(mapping?.owner?.ownerKhasraMappings)) {
          mapping.owner.ownerKhasraMappings.forEach((khasraMapping) => {
            allKhasraNumbers.push(khasraMapping.khasra.number);
          });
        }
      });
    }

    // Remove duplicates using Set
    allKhasraNumbers = [...new Set(allKhasraNumbers)];

    // Convert all khasra numbers to a single string
    const numbers =
      allKhasraNumbers.length > 0
        ? `${allKhasraNumbers.join(', ')} ${item?.state === 'Karnataka' || item?.state === 'Gujarat'
          ? '(survey)'
          : item?.state?.toLowerCase() === 'west bengal'
            ? '(dag)'
            : '(khasra)'
        }`
        : 'N/A';

    // Calculate final area for top-level "land" data
    const { finalMainArea, finalMainAreaType, finalSubArea, finalSubAreaType } = calculateFinalArea(
      item?.total_mainArea,
      item?.total_mainAreaType,
      item?.total_subArea,
      item?.total_subAreaType
    );

    // Build display string for total area
    let displayArea = 'N/A';

    if (finalMainArea || finalSubArea) {
      if (parseFloat(finalSubArea) === 0) {
        // If sub area is 0, don't show it
        displayArea = `${formatNumber(finalMainArea)} ${finalMainAreaType}`;
      } else {
        displayArea = `${formatNumber(finalMainArea)} ${finalMainAreaType}, ${formatNumber(
          finalSubArea
        )} ${finalSubAreaType}`;
      }
    }

    // Append (taluk) or (tehsil)
    const tehsilDisplay =
      item?.tehsil +
      ' ' +
      (item?.state === 'Karnataka' || item?.state === 'Gujarat' ? '(taluk)' : '(tehsil)') ||
      'N/A';

    return transformData(
      {
        ...item,
        number: numbers,
        area: displayArea,
        tehsil: tehsilDisplay
      },
      landsFormat
    );
  });

  /**
   * Event handlers
   */
  const handleOnSubmitAddLand = (data) => {
    mutateLand(data);
  };

  const handleToggleAddLand = useCallback(() => {
    setIsOpenAddLand((prev) => !prev);
  }, []);

  const handleToggleEditLand = useCallback((id) => {
    setLandsStaticId(id);
    setIsOpenEditLand((prev) => !prev);
  }, []);

  if (isError) {
    toast.error('Error fetching lands');
    navigate('/');
    return null;
  }

  //delete functions

  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckLandDelete(id),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (res, id) => {
      setIsSubmitting(false);
      const land = filterLand?.find((item) => parseInt(item.id) === parseInt(id));
      const landName = land?.name || 'Land';
      if (res.canDelete) {
        setCanDelete('yes');
        setResData({ name: landName });
        setSelectedIds([parseInt(id)]);
      } else {
        // Fallback if not found
        setResData({ ...res, name: landName });
        setCanDelete('no');
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error checking dependencies', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteLand(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Land Deleted Successfully');
      refetchLands();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error deleting land', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting land');
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  return (
    <>
      <Loader isVisible={isSubmitting || isLoading} />
      {isOpenAddLand && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Add Land'}
          onClose={handleToggleAddLand}
          width={88}
          upShift>
          <AddLand
            isSubmitting={isSubmitting}
            onSubmit={handleOnSubmitAddLand}
            onClose={handleToggleAddLand}
          />
        </Modal>
      )}

      {isOpenEditLand && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Edit Land'}
          onClose={handleToggleEditLand}
          width={88}
          upShift>
          <EditLand
            isSubmitting={isSubmitting}
            onSubmit={handleEditLand}
            id={LandsStaticId}
            onClose={handleToggleEditLand}
          />
        </Modal>
      )}

      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this land'}'`}
          onClose={() => setCanDelete(null)}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this land has dependencies, please delete following projects to proceed
            </p>
            <div className="flex justify-start, align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {resData &&
                resData.reason?.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < resData.reason.length - 1 ? ', ' : ''}
                  </span>
                ))}
            </div>
          </div>
        </Modal>
      )}

      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={'land'}
            onClose={() => setCanDelete(null)}
            data={landsFormatted}
            type={'land'}
            setCanDelete={setCanDelete}
            onDeleteClick={HandleDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            }`}>
          <Header data={{ name: 'Lands' }} />
        </div>

        {/* Search and Add Button */}
        <div
          className={`flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100  shadow-sm  z-20  transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            }`}>
          <Search onChange={handleSearchChange} query={landQuery} />
        </div>

        <div className="fixed bottom-8 right-8 z-50">
          <Button
            displayType="primary"
            onClick={() => {
              handleToggleAddLand();
            }}
            customStyle={{
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
            <Plus size={24} color="white" />
          </Button>
        </div>

        <GridWrapper width={100}>
          {/* Table content */}
          <div className="mt-[10rem] relative table-container h-[calc(100vh-11.5rem)] overflow-auto">
            <style>{`
              .table-container .table-body {
                position: static !important;
                overflow-y: visible !important;
                max-height: none !important;
                height: auto !important;
                top: auto !important;
                padding-bottom: 0 !important;
              }
              .table-container .table-container {
                height: auto !important;
              }
              .table-container > div {
                height: auto !important;
              }

            `}</style>
            <Table
              headers={[
                'Land Name',
                'Allotment Number',
                'Total Area',
                'Adminstrative Division',
                'Action'
              ]}
              identifier={'id'}
              //isViewEnable={true}
              handleRowClick={(id) => navigate(`/builder/management/land/${id}`)}
              onEdit={(id) => handleToggleEditLand(id)}
              data={landsFormatted}
              isDeleteEnable={true}
              onDelete={handleCheckDelete}
              isWrap={true}
            />
          </div>
        </GridWrapper>
      </Layout>
    </>
  );
};

export default LandsList;
